import { useEffect, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { t, L } from "../lib/translations";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import {
  Trash2, ShoppingBag, Coins, ArrowLeft, CheckCircle, Shield, Eye,
  Users, Heart, MessageSquare, Package, Wallet, Music4,
  ShoppingCart, ChevronRight, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtIP = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const CATEGORY_CFG = {
  followers:   { Icon: Users,         color: "#833AB4" },
  likes:       { Icon: Heart,         color: "#E1306C" },
  comments:    { Icon: MessageSquare, color: "#405DE6" },
  views:       { Icon: Eye,           color: "#0EA5E9" },
  story_views: { Icon: Eye,           color: "#8B5CF6" },
  story_likes: { Icon: Heart,         color: "#EC4899" },
  bundle:      { Icon: Package,       color: "#F77737" },
  music:       { Icon: Music4,        color: "#22c55e" },
};

export default function Cart() {
  const { lang }                         = useLanguage();
  const { user, checkAuth }              = useAuth();
  const { items, fetchCart, removeItem } = useCart();
  const navigate                         = useNavigate();
  const [ordering, setOrdering]          = useState(false);
  const [ordered, setOrdered]            = useState(false);
  const [termsAccepted, setTermsAccepted]= useState(false);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const total     = items.reduce((sum, i) => sum + (i.total_ipoints || 0), 0);
  const balance   = user?.ipoints || 0;
  const canAfford = balance >= total && items.length > 0;
  const deficit   = total - balance;

  const placeOrder = async () => {
    setOrdering(true);
    try {
      const res  = await fetch(`${API}/orders`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(t("cart.order_created", lang));
      setOrdered(true);
      checkAuth();
      fetchCart();
    } catch (err) {
      toast.error(err.message || t("Cart.order_failed_try_again", lang));
    } finally {
      setOrdering(false);
    }
  };

  /* ── Success screen ── */
  if (ordered) {
    return (
      <div className="min-h-screen pt-14 flex items-center justify-center px-4" data-testid="order-success">
        <div className="text-center max-w-sm w-full">
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6"
            style={{
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              boxShadow: "0 12px 32px rgba(34,197,94,0.4)",
            }}>
            <CheckCircle className="w-10 h-10" style={{ color: "#fff" }} />
          </div>
          <h2 className="text-2xl font-black mb-2 font-outfit" style={{ color: "var(--text-primary)" }}>
            {t("cart.order_created", lang)}
          </h2>
          <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
            {t("Cart.your_order_is_being_processed", lang)}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/dashboard?tab=orders&order=latest")}
              className="h-11 px-6 text-sm font-bold rounded-xl flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #8B5CF6, #4F46E5)", color: "#fff", boxShadow: "0 4px 16px rgba(139,92,246,0.4)" }}
              data-testid="go-to-orders-btn">
              <Package className="w-4 h-4" />{t("dash.orders", lang)}
            </button>
            <button onClick={() => navigate("/shop")}
              className="h-11 px-6 text-sm font-bold rounded-xl flex items-center justify-center gap-2 transition-all hover:opacity-80"
              style={{ background: "var(--surface-2)", color: "var(--text-primary)", border: "1px solid var(--border-color)" }}>
              {t("nav.shop", lang)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-14 pb-20" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:opacity-70"
            style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
            <ArrowLeft className="w-4 h-4 rtl-flip" />
          </button>
          <div>
            <h1 className="text-2xl font-black font-outfit leading-tight" style={{ color: "var(--text-primary)" }}>
              {t("cart.title", lang)}
            </h1>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
              {items.length} {t("Cart.item_s", lang)}
            </p>
          </div>
        </div>

        {/* ── Empty state ── */}
        {items.length === 0 ? (
          <div className="rounded-2xl border p-12 text-center"
            style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                background: "linear-gradient(135deg, #E1306C 0%, #833AB4 100%)",
                boxShadow: "0 8px 24px rgba(225,48,108,0.35)",
              }}>
              <ShoppingBag className="w-7 h-7" style={{ color: "#fff" }} />
            </div>
            <p className="font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>{t("cart.empty", lang)}</p>
            <p className="text-xs mb-5" style={{ color: "var(--text-secondary)" }}>
              {t("Cart.browse_the_shop_to_add_services", lang)}
            </p>
            <button onClick={() => navigate("/shop")}
              className="h-10 px-6 text-sm font-bold rounded-full inline-flex items-center gap-2 transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #E1306C, #833AB4)", color: "#fff", boxShadow: "0 4px 14px rgba(225,48,108,0.4)" }}
              data-testid="go-shop-btn">
              <ShoppingCart className="w-4 h-4" />{t("nav.shop", lang)}
            </button>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-5 items-start">

            {/* ── LEFT: Items ── */}
            <div className="flex-1 min-w-0 space-y-3">

              {/* Public account notice */}
              <div className="relative overflow-hidden rounded-2xl p-3.5 flex items-start gap-3"
                style={{
                  background: "linear-gradient(135deg, rgba(247,119,55,0.15) 0%, rgba(225,48,108,0.1) 100%)",
                  border: "1px solid rgba(247,119,55,0.25)",
                }}>
                <Eye className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#F77737]" />
                <div>
                  <p className="text-xs font-bold text-[#F77737]">
                    {t("Cart.keep_your_instagram_account_public", lang)}
                  </p>
                  <p className="text-xs mt-0.5 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {t("Cart.private_accounts_will_cause_the_order_to_be_canc", lang)}
                  </p>
                </div>
              </div>

              {/* Item cards */}
              <div className="space-y-2.5">
                {items.map((item) => {
                  const cfg = CATEGORY_CFG[item.product?.category] || CATEGORY_CFG.bundle;
                  const { Icon, color } = cfg;
                  const isBundle = item.product?.category === "bundle";
                  const targetValue = item.target_url || item.instagram_link;
                  const commentCount = String(item.custom_comments || "").split("\n").filter(Boolean).length;
                  return (
                    <div key={item.cart_item_id}
                      className="relative rounded-2xl overflow-hidden group"
                      style={{
                        background: `linear-gradient(135deg, ${color} 0%, ${color}BB 100%)`,
                        boxShadow: `0 6px 18px ${color}45`,
                      }}
                      data-testid={`cart-item-${item.cart_item_id}`}>
                      {/* Legibility overlay */}
                      <div className="absolute inset-0 pointer-events-none"
                        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.32) 100%)" }} />
                      {/* Watermark */}
                      <div className="absolute -bottom-3 -end-3 opacity-[0.15] pointer-events-none select-none" style={{ color: "#fff" }}>
                        <Icon style={{ width: 72, height: 72 }} />
                      </div>

                      <div className="relative z-10 p-3.5 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: "rgba(255,255,255,0.2)" }}>
                          <Icon style={{ width: 18, height: 18, color: "#fff" }} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold leading-snug truncate"
                            style={{ color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.2)" }}>
                            {item.product?.name?.[lang] || item.product?.name?.en}
                          </p>
                          <div className="mt-0.5">
                            {isBundle ? (
                              <div className="space-y-0.5">
                                <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.75)" }}>👤 {item.instagram_link}</p>
                                {item.instagram_link_2 && <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.75)" }}>📸 {item.instagram_link_2}</p>}
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 flex-wrap">
                                {targetValue && <span className="text-xs truncate max-w-[160px]" style={{ color: "rgba(255,255,255,0.75)" }}>{targetValue}</span>}
                                {item.quantity > 0 && <span className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>×{(item.quantity || 0).toLocaleString()}</span>}
                                {commentCount > 0 && (
                                  <span className="flex items-center gap-1 text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
                                    <MessageSquare className="w-3 h-3" />{commentCount}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-black font-outfit flex items-center gap-1" style={{ color: "#fff" }}>
                            <Coins className="w-3.5 h-3.5" />
                            {fmtIP(item.total_ipoints)}
                            <span className="text-[11px] font-normal opacity-75">iP</span>
                          </span>
                          <button onClick={() => removeItem(item.cart_item_id)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}
                            data-testid={`remove-item-${item.cart_item_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add more */}
              <button type="button" onClick={() => navigate("/shop")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-start transition-all hover:opacity-80"
                style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(139,92,246,0.1)", color: "#8B5CF6" }}>
                  <ShoppingCart className="w-3.5 h-3.5" />
                </div>
                <span className="flex-1 text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>
                  {t("Cart.add_more_services", lang)}
                </span>
                <ChevronRight className="w-4 h-4 rtl-flip" style={{ color: "var(--text-secondary)" }} />
              </button>
            </div>

            {/* ── RIGHT: Summary ── */}
            <div className="w-full lg:w-72 flex-shrink-0 lg:sticky lg:top-20 space-y-3">

              {/* Totals card */}
              <div className="relative overflow-hidden rounded-2xl p-4 space-y-3"
                style={{
                  background: "linear-gradient(135deg, #F77737 0%, #E1306C 100%)",
                  boxShadow: "0 8px 24px rgba(247,119,55,0.4)",
                }}>
                <div className="absolute -end-4 -bottom-4 opacity-[0.12] pointer-events-none" style={{ color: "#fff" }}>
                  <Coins style={{ width: 88, height: 88 }} />
                </div>
                <p className="relative z-10 text-[11px] font-semibold uppercase tracking-wider"
                  style={{ color: "rgba(255,255,255,0.75)" }}>
                  {t("Cart.order_summary", lang)}
                </p>
                <div className="relative z-10 flex items-center justify-between">
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>{t("cart.total", lang)}</span>
                  <span className="font-black font-outfit text-xl flex items-center gap-1.5" style={{ color: "#fff" }}>
                    <Coins className="w-4 h-4" />{fmtIP(total)}
                    <span className="text-sm font-normal opacity-75">iP</span>
                  </span>
                </div>
                <div className="relative z-10 flex items-center justify-between pt-3"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>
                    <Wallet className="w-3.5 h-3.5" />{t("cart.your_balance", lang)}
                  </span>
                  <span className="font-bold font-outfit text-sm" style={{ color: "#fff" }}>
                    {fmtIP(balance)} iP
                  </span>
                </div>
                <div className="relative z-10 flex items-center justify-between pt-3"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>{t("cart.after_purchase", lang)}</span>
                  <span className="font-bold font-outfit text-sm"
                    style={{ color: canAfford ? "#86efac" : "#fca5a5" }}>
                    {fmtIP(balance - total)} iP
                  </span>
                </div>
              </div>

              {/* Not enough balance */}
              {!canAfford && (
                <div className="relative overflow-hidden rounded-2xl border p-3.5 text-center"
                  style={{
                    background: "linear-gradient(135deg, rgba(225,48,108,0.08) 0%, rgba(131,58,180,0.08) 100%)",
                    borderColor: "rgba(225,48,108,0.25)",
                  }}>
                  <AlertCircle className="w-5 h-5 mx-auto mb-2 text-[#E1306C]" />
                  <p className="text-sm font-bold text-[#E1306C] mb-1">{t("cart.not_enough", lang)}</p>
                  <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
                    {L(lang,
                      `Need ${fmtIP(deficit)} more iP`,
                      `Не хватает ${fmtIP(deficit)} iP`,
                      `חסרים ${fmtIP(deficit)} iP`
                    )}
                  </p>
                  <button onClick={() => navigate(`/topup?needed_ip=${Math.round(deficit * 100) / 100}`)}
                    className="w-full h-9 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                    style={{ background: "linear-gradient(135deg, #E1306C, #833AB4)", color: "#fff", boxShadow: "0 4px 12px rgba(225,48,108,0.4)" }}
                    data-testid="cart-topup-cta">
                    + {t("Cart.top_up_balance", lang)}
                  </button>
                </div>
              )}

              {/* Terms */}
              <label className="flex items-start gap-3 cursor-pointer rounded-2xl border px-4 py-3.5"
                style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}
                data-testid="terms-checkbox-label">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input type="checkbox" checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="sr-only" data-testid="terms-checkbox" />
                  <div className="w-5 h-5 rounded-md flex items-center justify-center transition-all"
                    style={{
                      background: termsAccepted ? "#8B5CF6" : "var(--surface-2)",
                      border: termsAccepted ? "1px solid #8B5CF6" : "1px solid var(--border-color)",
                    }}>
                    {termsAccepted && (
                      <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                        <path d="M1 4L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {L(lang,
                    <>I agree to the{" "}
                      <Link to="/terms" target="_blank" className="underline" style={{ color: "#8B5CF6" }}>Terms</Link>
                      {" "}&amp;{" "}
                      <Link to="/warranty" target="_blank" className="underline" style={{ color: "#8B5CF6" }}>Warranty Policy</Link>
                    </>,
                    <>Я согласен с{" "}
                      <Link to="/terms" target="_blank" className="underline" style={{ color: "#8B5CF6" }}>Условиями</Link>
                      {" "}и{" "}
                      <Link to="/warranty" target="_blank" className="underline" style={{ color: "#8B5CF6" }}>Гарантией</Link>
                    </>,
                    <>קראתי ומסכים ל
                      <Link to="/terms" target="_blank" className="underline mx-1" style={{ color: "#8B5CF6" }}>תנאי השימוש</Link>
                      ול
                      <Link to="/warranty" target="_blank" className="underline mx-1" style={{ color: "#8B5CF6" }}>האחריות</Link>
                    </>
                  )}
                </span>
              </label>

              {/* Terms hint */}
              {!termsAccepted && canAfford && (
                <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs"
                  style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.18)", color: "#8B5CF6" }}>
                  <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                  {t("Cart.accept_the_terms_to_continue", lang)}
                </div>
              )}

              {/* Checkout button */}
              <button onClick={placeOrder}
                disabled={!canAfford || ordering || !termsAccepted}
                className="w-full h-12 rounded-xl font-bold text-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #E1306C, #833AB4)",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(225,48,108,0.45)",
                }}
                data-testid="checkout-btn">
                {ordering
                  ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{t("common.loading", lang)}</>
                  : <><CheckCircle className="w-4 h-4" />{t("cart.checkout", lang)}</>
                }
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
